<template>
  <div
    id="pricing"
    class="mt-3"
    v-if="pricing">
    <form
      id="pricing-form"
      name="pricingForm"
      @submit.prevent="submitPricing(pricing)"
      novalidate>
      <!-- Page Header -->
      <div class="container-fluid">
        <div class="row">
          <div class="col">
            <div class="d-flex flex-column flex-sm-row align-items-center">
              <h3 class="mb-0 mr-3 text-center text-sm-left">
                Pricing Package: {{event.name}}
              </h3>
              <b-badge
                v-if="pricing.id"
                :variant="pricing.is_open ? 'success' : 'danger'">
                {{pricing.is_open ? 'Active' : 'Inactive'}}
              </b-badge>
            </div>
            <hr>
          </div>
        </div>
      </div>
      <!-- / Page Header -->

      <div class="container">
        <div class="row">
          <!-- Name -->
          <div class="col-sm">
            <label for="pricing-name">
              Name:
              <small class="text-danger">*</small>
            </label>
            <b-form-input
              id="pricing-name"
              ref="pricingName"
              name="pricingName"
              type="text"
              placeholder="Enter a name"
              v-model="pricing.label"
              :readonly="submitting">
            </b-form-input>
          </div>
          <!-- / Name -->

          <!-- Price -->
          <div class="col-sm mt-3 mt-sm-0">
            <label for="pricing-price">
              Price:
              <small class="text-danger">*</small>
            </label>
            <Number
              id="pricing-price"
              ref="pricingPrice"
              name="pricingPrice"
              type="currency"
              step="10"
              placeholder="Enter a price"
              v-model="pricing.price"
              :readonly="submitting"
            />
          </div>
          <!-- / Price -->
        </div>

        <div class="row mt-3">
          <!-- Description -->
          <div class="col-sm">
            <label for="pricing-description">Description:</label>
            <b-form-input
              id="pricing-description"
              ref="pricingDescription"
              name="pricingDescription"
              type="text"
              placeholder="Enter a description"
              v-model="pricing.description"
              :readonly="submitting">
            </b-form-input>
          </div>
          <!-- / Description -->

          <!-- Order -->
          <div class="col-sm mt-3 mt-sm-0">
            <label for="pricing-order">Order:</label>
            <Number
              id="pricing-order"
              ref="pricingOrder"
              name="pricingOrder"
              type="number"
              step="1"
              display-decimals="0"
              value-decimals="0"
              placeholder="Enter an order"
              v-model="pricing.order"
              :readonly="submitting"
            />
          </div>
          <!-- / Order -->
        </div>

        <div class="row mt-3">
          <!-- Min DateTime -->
          <div class="col-sm">
            <label for="pricing-mindatetime">Start:</label>
            <DateTimePicker
              id="pricing-mindatetime"
              ref="pricingMinDateTime"
              name="pricingMinDateTime"
              v-model="pricing.min_date_time"
              :readonly="submitting"
            />
          </div>
          <!-- / Min DateTime -->

          <!-- Max DateTime -->
          <div class="col-sm mt-3 mt-sm-0">
            <label for="pricing-maxdatetime">End:</label>
            <DateTimePicker
              id="pricing-maxdatetime"
              ref="pricingMaxDateTime"
              name="pricingMaxDateTime"
              v-model="pricing.max_date_time"
              :readonly="submitting"
            />
          </div>
          <!-- / Max DateTime -->
        </div>

        <div class="row mt-3">
          <!-- Min Age -->
          <div class="col-sm">
            <label for="pricing-minage">Min. Age:</label>
            <Number
              id="pricing-minage"
              ref="pricingMinAge"
              name="pricingMinAge"
              type="number"
              step="1"
              display-decimals="0"
              value-decimals="0"
              placeholder="Enter a minimum age"
              v-model="pricing.min_age"
              :readonly="submitting"
            />
          </div>
          <!-- / Min Age -->

          <!-- Max Age -->
          <div class="col-sm mt-3 mt-sm-0">
            <label for="pricing-maxage">Max. Age:</label>
            <Number
              id="pricing-maxage"
              ref="pricingMaxAge"
              name="pricingMaxAge"
              type="number"
              step="1"
              display-decimals="0"
              value-decimals="0"
              placeholder="Enter a maximum age"
              v-model="pricing.max_age"
              :readonly="submitting"
            />
          </div>
          <!-- / Max Age -->
        </div>

        <div class="corner-buttons">
          <!-- Save Pricing Button -->
          <b-button
            class="shadow"
            type="submit"
            variant="success"
            :disabled="submitting">
            <font-awesome-icon icon="save"></font-awesome-icon> Save
          </b-button>
          <!-- / Save Pricing Button -->

          <!-- Delete Pricing Button -->
          <b-button
            class="shadow ml-3"
            type="button"
            variant="danger"
            v-if="pricing.id"
            :disabled="submitting"
            @click="submitPricing(pricing.id)">
            <font-awesome-icon icon="trash"></font-awesome-icon> Delete Pricing
          </b-button>
          <!-- / Delete Pricing Button -->
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import DirtyForm from '../services/dirty-form';
import AppMixin from '../services/app-mixin';
import EventBus from '../services/event-bus';

export default {
  name: 'pricing',
  mixins: [
    DirtyForm,
    AppMixin,
  ],
  data() {
    return {
      pricing: false,
      pricingCopy: false,
      event: {},
      submitting: false,
    };
  },
  computed: {
    pricingDirty() {
      const pricingJSON = JSON.stringify(this.pricing);
      const pricingCopyJSON = JSON.stringify(this.pricingCopy);
      return pricingJSON !== pricingCopyJSON;
    },
    anyDirty() {
      return this.pricingDirty;
    },
  },
  methods: {
    submitPricing(pricing) {
      let pricingPromise;

      this.submitting = true;

      if (typeof pricing !== 'object') {
        pricingPromise = this.deletePricing(pricing);
      } else if (pricing.id) {
        pricingPromise = this.updatePricing(pricing);
      } else {
        pricingPromise = this.createPricing(pricing);
      }

      pricingPromise
        .then(() => {
          this.$router.push({
            name: 'event',
            params: {
              eventID: this.event.id,
              tab: 'pricing',
            },
          });
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });

      return pricingPromise;
    },
    createPricing(pricing) {
      this.showToast('Creating pricing...');

      return DataSvc.createPricing(this.event.id, pricing)
        .then((p) => {
          this.pricing = p;
          this.pricingCopy = _.cloneDeep(p);
          return this.delay(1000);
        })
        .then(() => {
          this.showToast('Pricing created!');
        });
    },
    updatePricing(pricing) {
      this.showToast('Updating pricing...');

      return DataSvc.updatePricing(pricing)
        .then((p) => {
          this.pricing = p;
          this.pricingCopy = _.cloneDeep(p);
          return this.delay(1000);
        })
        .then(() => {
          this.showToast('Pricing updated!');
        });
    },
    deletePricing(pricingID) {
      const confirmDelete = () => {
        this.showToast('Deleting pricing...');

        return DataSvc.deletePricing(pricingID)
          .then(() => this.delay(1000))
          .then(() => {
            this.showToast('Pricing deleted!');
          });
      };

      return this.showConfirm(
        'Are you sure you want to delete this pricing?',
        'Deleting an pricing',
      ).then(confirmDelete);
    },
  },
  created() {
    this.showGlobalLoader('Loading pricing...');

    const initPricing = () => {
      const { eventID, pricingID } = this.$route.params;
      let pricingPromise;

      // get event
      const eventPromise = DataSvc.getEvent(eventID)
        .then((event) => {
          this.event = event;

          if (this.loggedInUser.actingOrg.id !== event.org_id) {
            this.findAndSwitchActingOrg(event.org_id);
          }
        })
        .finally(() => {
          this.$nextTick(() => {
            // watch for org change and route to events page
            EventBus.$on('acting-org-changed', this.goToEvents);
          });
        });

      // get pricing
      if (pricingID === 'new') {
        pricingPromise = Promise.resolve({});
      } else {
        pricingPromise = DataSvc.getPricing(pricingID);
      }

      pricingPromise
        .catch(() => {
          this.showWarning('That pricing does not exist.');
          this.$router.replace({ name: 'events' });
        })
        .then((pricing) => {
          this.pricing = pricing;
          this.pricingCopy = _.cloneDeep(pricing);

          // focus first input
          this.$nextTick(() => {
            this.focusInput('pricingName');
          });
        });

      return Promise.all([eventPromise, pricingPromise]);
    };

    this.initPromise.then(initPricing).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToEvents);
  },
};
</script>

<style lang="scss"></style>
